// generate_pdf_preview + preview_confirm_gate — render the real PDF inline; the gate also requires a confirm tick.
import React, { useMemo, useState } from 'react';
import { FileText, RefreshCw, ExternalLink } from 'lucide-react';
import { StepCard, StepNav, ErrorLine, humanise, pdfUrlFor, writeback } from './_stepKit';
import { selectAcceptedLetterKey } from './_letterSelector';

export default function PdfPreviewStep({ action, value, onChange, onNext, onBack, canBack, capturedData }) {
  const p = action.params || {};
  const key = (p.letter_selector && selectAcceptedLetterKey(p.letter_selector, capturedData, null)) || p.template_key || p.pdf_key || '';
  const isGate = action.type === 'preview_confirm_gate';
  const [bust, setBust] = useState(0);
  const url = useMemo(() => pdfUrlFor(key, capturedData), [key, capturedData, bust]); // eslint-disable-line react-hooks/exhaustive-deps
  const [ok, setOk] = useState(!!value?.confirmed);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const next = async () => {
    const out = { key, previewed_at: new Date().toISOString(), confirmed: isGate ? ok : undefined, has_design: !!url };
    setBusy(true); setErr('');
    try {
      await writeback(capturedData, isGate ? 'preview_confirmations' : 'pdf_previews', out, key);
      onChange(out); onNext(out);
    } catch (e) { setErr(e?.response?.data?.detail || e.message || 'Save failed'); } finally { setBusy(false); }
  };

  return (
    <div>
      <StepCard icon={FileText} title={`${isGate ? 'Preview & confirm' : 'Preview'} · ${humanise(key)}`} testid={isGate ? 'wf-preview-gate' : 'wf-pdf-preview'}>
        {url ? (
          <>
            <div className="flex gap-2">
              <button type="button" onClick={() => setBust((b) => b + 1)} data-testid="wf-pdf-refresh"
                className="px-3 py-1.5 text-xs rounded border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1"><RefreshCw size={12} /> Refresh</button>
              <a href={url} target="_blank" rel="noreferrer" data-testid="wf-pdf-open"
                className="px-3 py-1.5 text-xs rounded border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1"><ExternalLink size={12} /> Open in new tab</a>
            </div>
            <iframe title={key} src={url} className="w-full h-[560px] border border-slate-200 rounded" data-testid="wf-pdf-frame" />
          </>
        ) : (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded p-3" data-testid="wf-pdf-no-design">
            There's no PDF design for “{humanise(key)}” yet. Add one in Script Audit Studio, or continue without a preview.
          </div>
        )}
        {isGate && (
          <label className="flex items-center gap-2 p-3 border border-slate-200 rounded cursor-pointer">
            <input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} data-testid="wf-preview-gate-confirm" />
            <span className="text-sm text-slate-700">I've checked this {url ? 'PDF' : 'step'} and it's ready to send</span>
          </label>
        )}
        <ErrorLine err={err} testid="wf-pdf-err" />
      </StepCard>
      <StepNav canBack={canBack} onBack={onBack} onNext={next} busy={busy} disabled={isGate && !ok} />
    </div>
  );
}
